type ErrorAudience = 'public' | 'admin';

type UserFacingErrorOptions = {
  audience?: ErrorAudience;
};

const DIRECT_MAPPINGS: Array<{ pattern: RegExp; message: string }> = [
  {
    pattern: /reacceptance_required|consent|legal/i,
    message: 'Vous devez valider les consentements requis avant de continuer.',
  },
  {
    pattern: /permission-denied|unauthenticated|admin only|forbidden|not authorized/i,
    message: "Vous n'avez pas l'autorisation pour effectuer cette action.",
  },
  {
    pattern: /mollie|payment|paiement.*(failed|expire|échou)|checkout|card/i,
    message: 'Le paiement n\'a pas pu être finalisé. Vérifiez vos informations puis réessayez.',
  },
  {
    pattern: /iban|bank account|compte bancaire/i,
    message: 'Les coordonnées bancaires sont invalides ou incomplètes. Vérifiez-les puis réessayez.',
  },
  {
    pattern: /booking|reservation|outingid mismatch|bookingid/i,
    message: 'La réservation n\'a pas pu être traitée. Réessayez dans quelques instants.',
  },
];

const TECHNICAL_MARKERS = [
  /https? callable/i,
  /functions\//i,
  /idempotency/i,
  /\bout(?:ing)?id\b/i,
  /\bbookingid\b/i,
  /\bmismatch\b/i,
  /\bdetails\b\s*:/i,
  /\bcode\b\s*:/i,
  /^[A-Z0-9_]+$/,
];

function extractRawMessage(error: unknown): string {
  let message: unknown;

  if (typeof error === 'object' && error !== null) {
    if ('details' in error && typeof (error as { details?: unknown }).details === 'object' && (error as { details?: unknown }).details !== null) {
      const details = (error as { details?: { message?: unknown } }).details;
      message = details?.message;
    }

    if (message === undefined && 'message' in error) {
      message = (error as { message?: unknown }).message;
    }
  }

  return typeof message === 'string' ? message.trim() : '';
}

export function getUserFacingErrorMessage(
  error: unknown,
  fallbackMessage: string,
  options: UserFacingErrorOptions = {},
): string {
  const audience = options.audience || 'public';
  const rawMessage = extractRawMessage(error);

  if (!rawMessage) {
    return fallbackMessage;
  }

  for (const mapping of DIRECT_MAPPINGS) {
    if (mapping.pattern.test(rawMessage)) {
      if (audience === 'admin' && /permission-denied|unauthenticated|admin only|forbidden|not authorized/i.test(rawMessage)) {
        return 'Action non autorisée pour ce profil.';
      }
      return mapping.message;
    }
  }

  if (TECHNICAL_MARKERS.some((pattern) => pattern.test(rawMessage))) {
    return fallbackMessage;
  }

  if (audience === 'admin') {
    return 'Action impossible pour le moment. Vérifiez les informations saisies puis réessayez.';
  }

  return fallbackMessage;
}
